/**
 * User-facing copy for Izba Admin → Config → Account Plans, in one place so it
 * can move to i18n later (react-i18next is not installed in this repo).
 */
import { SPONSOR_STEP } from "../AccountPlanContainer/flowCopy";

export const S = {
  title: "Account Plans",
  subtitle:
    "The briefing questions and sections every account plan is turned into. Each tenant follows the ExecBridge default until you customise it.",
  editing: "Editing",
  defaultScope: "ExecBridge default (all tenants)",
  followsDefault: "follows default",
  inactive: "inactive",
  retry: "Retry",
  loading: "Loading…",

  liveCode: "Live: ExecBridge default from code",
  liveVersion: (n: number, by: string) => `Live: v${n}, published by ${by}`,
  followsDefaultPill: (n: number | null) =>
    n ? `Follows the default (v${n})` : "Follows the default",
  sameAsDefault: "Same as default",
  customisedCount: (n: number) => `${n} customised`,
  unsavedEdits: "Unsaved edits",
  draftSaved: (by: string, when: string) => `Draft saved by ${by}, ${when}`,
  unpublished: (n: number) =>
    n === 0
      ? "No changes from live"
      : `${n} change${n === 1 ? "" : "s"} not published`,
  saveDraft: "Save draft",
  revertEdits: "Undo unsaved edits",
  discardDraft: "Discard draft",
  reviewPublish: "Review & publish",
  scopeDraftMark: "draft",
  tenantFollows: "follows default",
  tenantCustomised: (n: number) => `customised (${n})`,

  tabQuestions: "Briefing questions",
  tabSections: "Briefing sections",
  tabPublish: "Review & publish",

  // origin
  fromDefault: "From default",
  customised: "Customised",
  addedForTenant: "Added for this tenant",
  newItem: "New",
  off: "Off",
  hidden: "Hidden",

  // questions
  questionsTitle: "Briefing questions",
  questionsHint: `Answered from every plan, in this order, on the ${SPONSOR_STEP.qa} step. A published briefing keeps the questions it was answered with. Prompts are edited in the admin console, not here.`,
  builtinQuestion: "Built-in question",
  customQuestion: "Custom question",
  untitledQuestion: "New question",
  questionLabel: "Question",
  questionPlaceholder: "What should every briefing answer?",
  askThis: "Ask this question",
  informsLabel: "Informs sections",
  informsHelp:
    "If the plan doesn’t answer this question, these sections show it as a gap.",
  informsMeta: (n: number) => `informs ${n} section${n === 1 ? "" : "s"}`,
  addQuestion: "Add question",

  // sections
  sectionsTitle: "Briefing sections",
  sectionsHint:
    "The sponsor briefing, top to bottom. The account header always comes first, and custom sections come after the standard ones.",
  sectionsHintTenant:
    "The sponsor briefing, top to bottom. The standard sections keep the ExecBridge default’s titles and order; for this tenant you can hide them, set who verifies them, and add custom sections after them.",
  standardTitleLocked:
    "A standard section’s title is set in the ExecBridge default, so every tenant’s briefing reads the same.",
  systemFacts: "From account data · no owner",
  ownerMeta: (owner: string) => `Verified by ${owner}`,
  upTo: (n: number) => `up to ${n} items`,
  collapsedCsm: "collapsed for CSM",
  customSection: "Custom section",
  untitledSection: "New section",
  titleLabel: "Title",
  instructionsLabel: "What to pull from the plan",
  instructionsPlaceholder:
    "Describe what belongs in this section, as you would to an analyst reading the deck.",
  instructionsHelp:
    "Each item must cite a slide and quote it, like Strategic priorities. Items whose quote isn’t on that slide are dropped.",
  builtinNote:
    "What goes into a built-in section is set by the Sponsor View prompt and checked in code, so only its title and placement are edited here.",
  builtinNoteTenant:
    "What goes into a built-in section is set by the Sponsor View prompt and checked in code. For this tenant you can hide it and set who verifies it.",
  ownerLabel: "Verified by",
  maxItemsLabel: "Max items",
  collapsedLabel: "Collapsed for CSM",
  showInBriefing: "Show in briefing",
  addSection: "Add custom section",

  // row actions
  moveUp: "Move up",
  moveDown: "Move down",
  reset: "Reset to default",
  remove: "Remove",

  // preview
  previewTitle: "What they see",
  previewHint: "This tenant’s briefing with your edits",
  personaSponsor: "Sponsor",
  personaAe: "AE",
  personaCsm: "CSM",
  // The sponsor's step names, as the plan page shows them (F-UF-F21).
  briefing: SPONSOR_STEP.sponsor,
  qaBreakdown: SPONSOR_STEP.qa,
  pvHidden: "Hidden for this tenant",
  pvSystem: "From account data",
  pvCollapsed: "Collapsed",
  pvCustom: "Custom · cited items",
  pvCited: "Cited from the plan",
  youVerify: "You verify",
  custom: "Custom",

  // publish
  changesTitle: "Changes since live",
  changesHint: (target: string) =>
    `Publishing makes these live for ${target}. Briefings already generated keep what they were made with.`,
  noChanges: "No changes yet. Edit questions or sections, then come back here.",
  added: "Added",
  changed: "Changed",
  removed: "Removed",
  question: "Question",
  section: "Section",
  orderChanged: (what: string) => `${what} order changed`,
  notYetUsed:
    "New briefings for these tenants are generated with the live template: new uploads and regenerated drafts. Briefings already generated keep the template they were made with.",
  defaultFanout:
    "Tenants that follow the default get it when you publish. Customised tenants keep their own template.",
  publishTitle: "Publish",
  evalLabel: "Signed eval result",
  evalHelp:
    "Publishing needs a passing eval of exactly this draft, with the prompts live now. Paste the signed result from the admin console. (The admin console can’t run template evals yet; until it can, use the emergency option below.)",
  evalPlaceholder:
    "{ \u201cpayload\u201d: { … }, \u201csig\u201d: \u201c…\u201d }",
  evalRead: (id: string) =>
    `Read eval ${id || "(no id)"}. ExecBridge checks it on publish.`,
  evalUnreadable:
    "This isn’t a signed eval result. Paste the JSON exactly as the admin console gives it.",
  forceTitle: "Publish without an eval (emergency)",
  forceHint:
    "Skips the eval gate. The reason is kept with the version and shown in its history, and the publish is logged.",
  forceReasonLabel: "Why it can’t wait for an eval",
  forceReasonPlaceholder: "At least a sentence",
  forcePublish: "Publish without eval",
  evaluated: "Evaluated",
  noEval: "No eval",
  forcedBecause: (reason: string) => `Published without an eval: ${reason}`,
  noteLabel: "Note",
  notePlaceholder: "What changed and why",
  publishFor: (target: string) => `Publish for ${target}`,
  confirmPublish: (target: string) => `Confirm: publish for ${target}`,
  cancel: "Cancel",
  theDefault: "every tenant on the default",
  historyTitle: "History",
  historyHint:
    "Every publish for this template. Rolling back publishes that version again.",
  noHistory: (isDefault: boolean) =>
    isDefault
      ? "Nothing published yet: the template in code is live."
      : "Nothing published yet: this tenant follows the default.",
  rollBackTo: (n: number) => `Roll back to v${n}`,
  liveNow: "Live",
  actionPublish: "Published",
  actionRollback: "Rolled back",
  actionFollow: "Followed the default",
  followDefault: "Follow the default again",
  followDefaultHint:
    "Drops this tenant’s own template. It will use the default, and get the default’s future changes.",
};
